import { get } from 'lodash/fp';
import { createSelector } from 'reselect';
import { CommanderThunkAction, WithAccountId } from '@mews-commander/types';
import {
    createAction,
    createAsyncActionTypes,
    createAsyncMetaSelectors,
    createScopedReducer,
    createScopedSelector,
} from '@mews/framework';
import { fetchCurrentEnterpriseBillsByIds } from '@mews-commander/legacy-entities/src/actions/bills';
import { META_STATE_SUCCEEDED } from '@mews-commander/legacy-entities/src/constants';
import { createEntityAsyncMetaHandlers } from '@mews-commander/legacy-entities/src/ducks/utils';
import { fetchAccountsByBillIds } from '@mews-commander/legacy-entities/src/actions/accounts';
import { fetchCurrentEnterpriseReservationsByReservationIds } from '@mews-commander/legacy-entities/src/actions/reservations';
import { BillExtent } from '@mews-commander/data-access';
import { fetchProductServiceOrders } from '@mews-commander/legacy-entities/src/actions/productServiceOrders';
import { BillClassification } from '../constants';
import { fetchOpenServiceOrderNotesByBillIds } from '../actions/billing';
import { getProductServiceOrderIds } from '../selectors';
import { moduleSelector as parentSelector, prefix } from '../module';
import { getLinkedReservationIdsByBillIds } from './reservations';

export const NAME = 'bills';

const BILL_FETCH = prefix('BILL_FETCH');
export const [BILL_FETCH_STARTED, BILL_FETCH_SUCCEEDED, BILL_FETCH_FAILED] = createAsyncActionTypes(BILL_FETCH);

export const refetchBill = ({ accountId, billId, scope, extent = {} }: $TSFixMe): CommanderThunkAction =>
    refetchBills({ accountIds: accountId ? [accountId] : undefined, billIds: billId ? [billId] : [], scope, extent });

export const refetchBills =
    ({
        accountIds,
        billIds,
        scope,
        extent,
        reservationGroupId,
    }: {
        accountIds?: string[];
        billIds: string[];
        scope: string;
        extent: Partial<BillExtent>;
        reservationGroupId?: string;
    }): CommanderThunkAction =>
    async (dispatch, getState) => {
        const promise = dispatch(
            fetchCurrentEnterpriseBillsByIds({
                accountIds,
                billIds,
                extent: extent as BillExtent,
                reservationGroupId,
            })
        );

        return dispatch(createAction(BILL_FETCH, promise, { scope })).then(async () => {
            const state = getState();
            const linkedReservationIds = getLinkedReservationIdsByBillIds(state, { billIds });
            const productServiceOrderIds = getProductServiceOrderIds(state, { billIds });

            if (productServiceOrderIds.length > 0) {
                // refetching notes for product service orders
                await dispatch(fetchProductServiceOrders({ productServiceOrderIds, extent: { notes: true } }));
            }

            await dispatch(fetchOpenServiceOrderNotesByBillIds(billIds));

            await dispatch(
                fetchCurrentEnterpriseReservationsByReservationIds({
                    reservationIds: linkedReservationIds,
                })
            );

            return dispatch(fetchAccountsByBillIds({ billIds }));
        });
    };

const initialState = META_STATE_SUCCEEDED;

export const billsReducer = createScopedReducer(
    initialState,
    createEntityAsyncMetaHandlers({
        STARTED: BILL_FETCH_STARTED,
        SUCCEEDED: BILL_FETCH_SUCCEEDED,
        FAILED: BILL_FETCH_FAILED,
    })
);

const moduleSelector = createSelector(parentSelector, get(NAME));

const getScope = createScopedSelector({ parentSelector: moduleSelector, initialState });

const getMeta = createSelector(getScope, get('meta'));

export const [isBillFetching] = createAsyncMetaSelectors(getMeta);

export const getBillClassification = ({
    billOwnerId,
    accountId,
}: { billOwnerId?: string } & Partial<WithAccountId>) => {
    if (billOwnerId === undefined || accountId === undefined) {
        return undefined;
    }
    return billOwnerId === accountId ? BillClassification.Owned : BillClassification.Associated;
};
